// @ds/config/actionContext — the runtime context handed to every action/handler.
//
// Leaf module (no imports from sibling config modules) so both `displayLayout`
// (layout behavior slots) and `sectionConfig` (ActionSpec) can depend on it
// without a cycle. The dispatcher builds the concrete value once and passes it
// to each handler; actions own their behavior, layouts own their presentation.

import type { ReactNode } from "react";

export interface ActionContext {
  /** Navigate to another entity's detail route. */
  navigate: (
    type: string,
    id: number | "new",
    opts?: { newTab?: boolean },
  ) => void;
  /**
   * Imperative confirm dialog (AntD-hosted — nothing for layouts/shell to
   * mount). Resolves true on confirm, false on cancel. Lets a destructive
   * handler own its own confirmation.
   */
  confirm: (opts: {
    titleKey: string;
    body?: ReactNode;
    okKey?: string;
    danger?: boolean;
  }) => Promise<boolean>;
}

/**
 * A row behavior — pure logic, no presentation. The LAYOUT decides icon /
 * trigger / placement and calls this with the row, the shared `ctx`
 * (navigate + confirm), and the section's scoped `state` (handlers/data from
 * `useSectionState`).
 */
export type RowHandler<TRow, State = unknown> = (
  row: TRow,
  ctx: ActionContext,
  state: State,
) => void | Promise<void>;

/**
 * Wrap a destructive row handler so it confirms first (via `ctx.confirm`)
 * before running. Confirmation lives in the behavior, not the layout/shell.
 */
export function confirmRowDelete<TRow, State = unknown>(
  onDelete: (row: TRow, state: State) => void | Promise<void>,
  opts?: { titleKey?: string; okKey?: string },
): RowHandler<TRow, State> {
  return async (row, ctx, state) => {
    const ok = await ctx.confirm({
      titleKey: opts?.titleKey ?? "common.confirm_delete",
      okKey: opts?.okKey ?? "common.actions.delete",
      danger: true,
    });
    if (ok) await onDelete(row, state);
  };
}
